// AddPlat.js
import React, { useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { motion } from "framer-motion";
import { FaPlus, FaTrash } from "react-icons/fa";
import Swal from "sweetalert2";
import Loader from "../../pages/Loader";

const AddPlot = () => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const MAX_IMAGES = 10;

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    const totalImages = selectedImages.length + files.length;

    if (totalImages > MAX_IMAGES) {
      Swal.fire({
        icon: "warning",
        title: "Image Limit Reached",
        text: `You can only upload a maximum of ${MAX_IMAGES} images.`,
        confirmButtonColor: "#f59e0b",
      });

      // Allow only remaining count
      const allowedFiles = files.slice(0, MAX_IMAGES - selectedImages.length);
      setSelectedImages((prev) => [...prev, ...allowedFiles]);
    } else {
      setSelectedImages((prev) => [...prev, ...files]);
    }

    // Reset input to allow re-upload of same file
    // e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    const updatedImages = selectedImages.filter((_, i) => i !== index);
    setSelectedImages(updatedImages);
  };


  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      distances: [{ distanceFrom: "", locality: "" }],
      keyFeatures: [{ value: "" }],
    },
  });

  const {
    fields: distanceFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "distances",
  });

  const {
    fields: keyFeatureFields,
    append: appendKeyFeature,
    remove: removeKeyFeature,
  } = useFieldArray({
    control,
    name: "keyFeatures",
  });

  const onSubmit = async (data) => {
    try {
      const uploadedUrls = [];
      setLoading(true);
      const uploadPromises = selectedImages.map((file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "prayagproperties");

        return fetch("https://api.cloudinary.com/v1_1/djmyigser/image/upload", {
          method: "POST",
          body: formData,
        }).then((res) => res.json());
      });

      const cloudinaryResults = await Promise.all(uploadPromises);

      // Check for errors and extract URLs
      for (const result of cloudinaryResults) {
        if (!result.secure_url) {
          throw new Error("One or more uploads failed.");
        }
        uploadedUrls.push(result.secure_url);
      }

      const payload = {
        title: data.title,
        address: data.address,
        description: data.description,
        price: data.price,
        size: data.size,
        availability: data.availability,
        // approved: data.approved,
        contactNo: data.contactNo,
        sellerName: data.sellerName,
        soilType: data.soilType,
        facing: data.facing,
        location: data.address, // or separate location input
        photos: uploadedUrls,
        keyFeatures: data.keyFeatures.map((item) => item.value),
        distances: data.distances || [], // Make sure you're passing this correctly
      };


      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/create_plot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await res.json();

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Plot Added",
          text: responseData.message || "The Plot was added successfully!",
          confirmButtonColor: "#2563eb",
        });
        reset();
        setSelectedImages([]);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            responseData.message || "Something went wrong while adding plot.",
          confirmButtonColor: "#ef4444",
        });
      }
    } catch (error) {
      console.error("News submission error:", error);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to submit plot. Please try again later.",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const fields = [
    { label: "Title", name: "title", type: "text", required: true },
    { label: "Address", name: "address", type: "text", required: true },
    {
      label: "Description",
      name: "description",
      type: "textarea",
      required: true,
    },
    { label: "Price", name: "price", type: "number", required: true },
    { label: "Size (sqft)", name: "size", type: "text", required: true },
    {
      label: "Availability",
      name: "availability",
      type: "select",
      options: ["Yes", "No"],
      required: true,
    },
    // { label: "Approved By", name: "approved", type: "text" },

    { label: "Contact No", name: "contactNo", type: "text", required: true },
    { label: "Seller Name", name: "sellerName", type: "text", required: true },
    { label: "Soil Type", name: "soilType", type: "text", required: true },
    {
      label: "Facing Direction",
      name: "facing",
      type: "text",
      required: true,
    },
    {
      label: "Upload an Image",
      name: "photos",
      type: "file",
      required: true,
    },
  ];

  return (
    <>
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-4">
        <motion.div
          className="w-full max-w-6xl bg-white rounded-xl shadow-lg overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          {/* Gradient header */}
          <div className="bg-gradient-to-r from-blue-700 to-cyan-500 px-6 py-4">
            <h2 className="text-2xl font-bold text-white">Add Plot Details</h2>
          </div>

          {/* Form content */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="p-6 md:p-10 grid gap-6 grid-cols-1 md:grid-cols-2 bg-gray-50"
          >
            {fields.map((field) => (
              <div key={field.name}>
                <label className="block mb-3 text-sm font-bold text-gray-700 ">
                  {field.label} <span className="text-red-500">*</span>
                </label>

                {field.type === "textarea" ? (
                  <textarea
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                    })}
                    rows={3}
                    className={`w-full resize-none border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors[field.name]
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />
                ) : field.type === "select" ? (
                  <select
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                    })}
                    defaultValue=""
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors[field.name]
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  >
                    <option value="" disabled>
                      Select {field.label}
                    </option>
                    {field.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : field.type === "file" ? (
                  <>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        selectedImages.length === 0
                          ? "border-gray-300 focus:ring-red-400"
                          : "border-gray-300 focus:ring-blue-400"
                      }`}
                    />

                    {selectedImages.length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-3">
                        {selectedImages.map((image, idx) => (
                          <div
                            key={idx}
                            className="relative w-24 h-24 border rounded-lg overflow-hidden shadow-sm"
                          >
                            <img
                              src={URL.createObjectURL(image)}
                              alt={`preview-${idx}`}
                              className="object-cover w-full h-full"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(idx)}
                              className="absolute top-1 right-1 bg-white p-1 rounded-full shadow hover:bg-red-100"
                              title="Remove"
                            >
                              <FaTrash className="text-red-600 text-xs" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <input
                    type={field.type}
                    {...register(field.name, {
                      required: field.required
                        ? `${field.label} is required`
                        : false,
                    })}
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors[field.name]
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />
                )}

                {errors[field.name] && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors[field.name]?.message}
                  </p>
                )}
              </div>
            ))}

            {/* Key Features Fields */}
            <div className="col-span-1">
              <label className="block font-semibold text-md text-gray-800 mb-2">
                Key Features <span className="text-red-500">*</span>
              </label>
              {keyFeatureFields.map((item, index) => (
                <div key={item.id} className="relative mb-4">
                  <input
                    placeholder="Enter Key Feature"
                    {...register(`keyFeatures.${index}.value`, {
                      required: "Key feature is required",
                    })}
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors?.keyFeatures?.[index]?.value
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />

                  {keyFeatureFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeKeyFeature(index)}
                      className="absolute right-3 top-2 text-red-500 hover:text-red-700"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => appendKeyFeature({ value: "" })}
                className="flex items-center text-blue-600 hover:underline mt-2"
              >
                <FaPlus className="mr-2" />
                Add Feature
              </button>
            </div>

            {/* Distance & Locality Fields */}
            <div className="col-span-1 md:col-span-2">
              <label className="block text-lg font-semibold text-gray-800 mb-2">
                Distance and Locality <span className="text-red-500">*</span>
              </label>
              {distanceFields.map((item, index) => (
                <div
                  key={item.id}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 relative"
                >
                  <input
                    placeholder="Locality"
                    {...register(`distances.${index}.locality`, {
                      required: "Locality is required",
                    })}
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors?.distances?.[index]?.locality
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />
                  <input
                    placeholder="Distance From"
                    {...register(`distances.${index}.distanceFrom`, {
                      required: "Distance is required",
                    })}
                    className={`w-full border px-4 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                      errors?.distances?.[index]?.distanceFrom
                        ? "border-red-500 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />

                  {distanceFields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="absolute right-3 top-2 text-red-500 hover:text-red-700"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => append({ distanceFrom: "", locality: "" })}
                className="flex items-center text-blue-600 hover:underline mt-2"
              >
                <FaPlus className="mr-2" />
                Add More
              </button>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading && (
                  // <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <Loader />
                )}
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>
  );
};

export default AddPlot;
